// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SuperadminService } from "@/services/superadmin";
import { FeatureFlagTable } from "@/components/superadmin/feature-flags/FeatureFlagTable";
import { CreateFlagDialog } from "@/components/superadmin/feature-flags/CreateFlagDialog";
import { EditFlagDialog } from "@/components/superadmin/feature-flags/EditFlagDialog";
import { FlagDetailSheet } from "@/components/superadmin/feature-flags/FlagDetailSheet";
import type { FeatureFlag } from "@/types/superadmin";

export interface SearchParams {
  page: number;
  search: string;
}

export const Route = createFileRoute("/superadmin/feature-flags")({
  component: FeatureFlagsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || "",
  }),
});

function FeatureFlagsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = Route.useSearch();

  // Local search input with debounce
  const [searchInput, setSearchInput] = useState(params.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== params.search) {
        updateParams({ search: searchInput, page: 1 });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: "/superadmin/feature-flags",
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editFlag, setEditFlag] = useState<FeatureFlag | null>(null);
  const [deleteFlag, setDeleteFlag] = useState<FeatureFlag | null>(null);
  const [detailFlagId, setDetailFlagId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Org search for override manager
  const [orgSearchTerm, setOrgSearchTerm] = useState("");

  // ── Queries ──────────────────────────────────────────────────────────

  const flagsQuery = useQuery({
    queryKey: ["superadmin", "featureFlags", params.page, params.search],
    queryFn: () =>
      SuperadminService.listFeatureFlags({
        page: params.page,
        search: params.search || undefined,
      }),
  });

  const flagDetailQuery = useQuery({
    queryKey: ["superadmin", "featureFlagDetail", detailFlagId],
    queryFn: () => SuperadminService.getFeatureFlag(detailFlagId!),
    enabled: !!detailFlagId,
  });

  const orgSearchQuery = useQuery({
    queryKey: ["superadmin", "orgSearch", orgSearchTerm],
    queryFn: () => SuperadminService.searchOrganizations(orgSearchTerm),
    enabled: orgSearchTerm.length > 0,
  });

  // ── Mutations ────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: SuperadminService.createFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      setCreateOpen(false);
      setCreateError(null);
      toast.success(t("featureFlags.create.success"));
    },
    onError: (err: { message?: string }) => {
      const msg = err.message ?? t("featureFlags.create.error");
      setCreateError(msg);
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: SuperadminService.updateFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      if (detailFlagId) {
        queryClient.invalidateQueries({
          queryKey: ["superadmin", "featureFlagDetail", detailFlagId],
        });
      }
      setEditFlag(null);
      toast.success(t("featureFlags.edit.success"));
    },
    onError: () => toast.error(t("featureFlags.edit.error")),
  });

  const deleteMutation = useMutation({
    mutationFn: SuperadminService.deleteFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      setDeleteFlag(null);
      toast.success(t("featureFlags.delete.success"));
    },
    onError: () => toast.error(t("featureFlags.delete.error")),
  });

  const toggleMutation = useMutation({
    mutationFn: SuperadminService.toggleFeatureFlag,
    onMutate: async (variables) => {
      // Optimistic update
      await queryClient.cancelQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      const prev = queryClient.getQueryData([
        "superadmin",
        "featureFlags",
        params.page,
        params.search,
      ]);
      queryClient.setQueryData(
        ["superadmin", "featureFlags", params.page, params.search],
        (old: typeof flagsQuery.data) => {
          if (!old) return old;
          return {
            ...old,
            featureFlags: old.featureFlags.map((f: FeatureFlag) =>
              f.id === variables.featureFlagId
                ? { ...f, defaultValue: variables.enabled }
                : f,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(
          ["superadmin", "featureFlags", params.page, params.search],
          context.prev,
        );
      }
      toast.error(t("featureFlags.toggle.error"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
    },
  });

  const setOverrideMutation = useMutation({
    mutationFn: SuperadminService.setOrgOverride,
    onSuccess: () => {
      if (detailFlagId) {
        queryClient.invalidateQueries({
          queryKey: ["superadmin", "featureFlagDetail", detailFlagId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      toast.success(t("featureFlags.overrides.success"));
    },
    onError: () => toast.error(t("featureFlags.overrides.error")),
  });

  const removeOverrideMutation = useMutation({
    mutationFn: SuperadminService.removeOrgOverride,
    onSuccess: () => {
      if (detailFlagId) {
        queryClient.invalidateQueries({
          queryKey: ["superadmin", "featureFlagDetail", detailFlagId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "featureFlags"],
      });
      toast.success(t("featureFlags.overrides.success"));
    },
    onError: () => toast.error(t("featureFlags.overrides.error")),
  });

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleToggle = useCallback(
    (flagId: string, enabled: boolean) => {
      toggleMutation.mutate({ featureFlagId: flagId, enabled });
    },
    [toggleMutation],
  );

  const handleViewOverrides = useCallback((flag: FeatureFlag) => {
    setDetailFlagId(flag.id);
  }, []);

  const handleAddOverride = useCallback(
    (organizationId: string, enabled: boolean) => {
      if (!detailFlagId) return;
      setOverrideMutation.mutate({
        featureFlagId: detailFlagId,
        organizationId,
        enabled,
      });
    },
    [detailFlagId, setOverrideMutation],
  );

  const handleRemoveOverride = useCallback(
    (organizationId: string) => {
      if (!detailFlagId) return;
      removeOverrideMutation.mutate({
        featureFlagId: detailFlagId,
        organizationId,
      });
    },
    [detailFlagId, removeOverrideMutation],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("featureFlags.title")}</h1>
        <p className="text-muted-foreground">
          {t("featureFlags.description")}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("featureFlags.searchPlaceholder")}
            className="ps-9"
          />
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t("featureFlags.create.title")}
        </Button>
      </div>

      {/* Table */}
      <FeatureFlagTable
        flags={flagsQuery.data?.featureFlags ?? []}
        pagination={flagsQuery.data?.pagination}
        isLoading={flagsQuery.isLoading}
        onPageChange={(page) => updateParams({ page })}
        onToggle={handleToggle}
        onEdit={setEditFlag}
        onViewOverrides={handleViewOverrides}
        onDelete={setDeleteFlag}
        isToggling={toggleMutation.isPending}
      />

      {/* Create Dialog */}
      <CreateFlagDialog
        open={createOpen}
        onOpenChange={(v) => {
          setCreateOpen(v);
          if (!v) setCreateError(null);
        }}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
        error={createError}
      />

      {/* Edit Dialog */}
      <EditFlagDialog
        open={!!editFlag}
        onOpenChange={(open) => !open && setEditFlag(null)}
        flag={editFlag}
        onSubmit={(data) => updateMutation.mutate(data)}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteFlag}
        onOpenChange={(open) => !open && setDeleteFlag(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.confirmAction")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("featureFlags.delete.confirm", { key: deleteFlag?.key })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteFlag && deleteMutation.mutate(deleteFlag.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Flag Detail Sheet */}
      <FlagDetailSheet
        open={!!detailFlagId}
        onOpenChange={(open) => !open && setDetailFlagId(null)}
        flag={flagDetailQuery.data ?? null}
        isLoading={flagDetailQuery.isLoading}
        onAddOverride={handleAddOverride}
        onRemoveOverride={handleRemoveOverride}
        isAddingOverride={setOverrideMutation.isPending}
        isRemovingOverride={removeOverrideMutation.isPending}
        orgSearchResults={
          orgSearchQuery.data?.organizations ?? []
        }
        onOrgSearch={setOrgSearchTerm}
        isSearchingOrgs={orgSearchQuery.isLoading}
      />
    </div>
  );
}
